import { useCallback, useMemo, useRef, useState } from "react";
import { GridCellParams, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  GENDEC_TARGET_TIME_MAX,
  GendecFormats,
  IFlightType,
  IGendecRuleErrors,
  IGendecRuleWire,
  validateGendecRule,
} from "@vmosolutions/oc-interfaces";
import { EditableDataGrid, dataCol, integerEditCell, useEditableGridPagination } from "src/components/EditableDataGrid";
import { AddRuleDialog } from "./AddRuleDialog";
import { FlightTypeTriStateField } from "./FlightTypeTriStateField";

/**
 * A rule row. `flightNumber` is always held as the string form in the grid (a single
 * editable text cell): a number ("919"), a range ("920-925"), a regex ("/^9/"), or
 * blank for "any". The backend's tolerant deserializer turns the string back into the
 * stored rich type on save, so the grid never has to deal with the polymorphic type.
 */
export interface IGendecRuleRow extends Omit<IGendecRuleWire, "flightNumber"> {
  _rowId: string;
  flightNumber: string;
}

/** Convert a wire flightNumber (number | {start,end} | "/re/" | null) to its editable string. */
export const flightNumberToCell = (fn: IGendecRuleWire["flightNumber"]): string => {
  if (fn === null || fn === undefined) return "";
  if (typeof fn === "number") return String(fn);
  if (typeof fn === "object" && "start" in fn && "end" in fn) return `${fn.start}-${fn.end}`;
  return fn; // already a "/regex/" (or other) string
};

const getRuleRowId = (row: IGendecRuleRow): string => row._rowId;

/** Fields whose cell reads a pair of arrays and commits through a popover rather than inline edit. */
const FLIGHT_TYPE_FIELD = "flightTypeFilter";
/** Non-data column holding the row action buttons; never highlighted. */
const ACTIONS_FIELD = "actions";

const sortedKey = (arr: string[] | undefined): string => (arr ?? []).slice().sort().join(",");

interface IRulesGridProps {
  rows: IGendecRuleRow[];
  /** The rows as last loaded/saved, keyed by the same _rowId, so changed cells can be highlighted. */
  originalRows: IGendecRuleRow[];
  onChange: (rows: IGendecRuleRow[]) => void;
  /**
   * All flight types from `state.structures.customerStructure.flightTypes`. Threaded
   * through from `ManageGenDec` so the tri-state Flight Type column can render names
   * and the picker popover.
   */
  flightTypes: IFlightType[];
}

export const RulesGrid = (props: IRulesGridProps): JSX.Element => {
  const { rows, originalRows, onChange, flightTypes } = props;
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const { paginationModel, onPaginationModelChange, requestFocusRow } = useEditableGridPagination({
    rows,
    getRowId: getRuleRowId,
    containerRef: gridContainerRef,
  });

  const originalById = useMemo(() => new Map(originalRows.map((r) => [r._rowId, r])), [originalRows]);

  // Airport idents are always uppercase in the stored shape; snap typed text up on commit
  // so the grid display matches the wire form. A "/regex/" token stays slash-delimited
  // and its (case-insensitive) regex still matches the same set after uppercasing.
  const normalizeRow = useCallback(
    (incoming: IGendecRuleRow): IGendecRuleRow => ({
      ...incoming,
      origin: (incoming.origin ?? "").toUpperCase(),
      destination: (incoming.destination ?? "").toUpperCase(),
    }),
    []
  );

  const handleAddRule = useCallback(
    (row: IGendecRuleRow) => {
      requestFocusRow(row._rowId, true);
      onChange([...rows, row]);
    },
    [rows, onChange, requestFocusRow]
  );

  const handleDuplicate = useCallback(
    (row: IGendecRuleRow) => {
      const copy: IGendecRuleRow = { ...row, _rowId: crypto.randomUUID() };
      const index = rows.findIndex((r) => r._rowId === row._rowId);
      const next = [...rows];
      next.splice(index + 1, 0, copy);
      requestFocusRow(copy._rowId, false);
      onChange(next);
    },
    [rows, onChange, requestFocusRow]
  );

  const handleDelete = useCallback(
    (row: IGendecRuleRow) => onChange(rows.filter((r) => r._rowId !== row._rowId)),
    [rows, onChange]
  );

  // Per-row validation errors, computed from the *current* row state so invalid edits the
  // user just made show up immediately (the grid no longer blocks the edit at commit time).
  const errorsByRowId = useMemo(() => {
    const map = new Map<string, IGendecRuleErrors>();
    rows.forEach((r) => map.set(r._rowId, validateGendecRule(r)));
    return map;
  }, [rows]);

  /**
   * A cell is dirty when its value differs from the loaded original, or the row is new.
   * The Flight Type cell compares both include and exclude sets as sorted arrays.
   */
  const isCellDirty = useCallback(
    (row: IGendecRuleRow, field: string): boolean => {
      if (field === ACTIONS_FIELD) return false;
      const original = originalById.get(row._rowId);
      if (!original) return true;
      if (field === FLIGHT_TYPE_FIELD) {
        return (
          sortedKey(row.flightTypeIds) !== sortedKey(original.flightTypeIds) ||
          sortedKey(row.excludedFlightTypeIds) !== sortedKey(original.excludedFlightTypeIds)
        );
      }
      return (row as unknown as Record<string, unknown>)[field] !== (original as unknown as Record<string, unknown>)[field];
    },
    [originalById]
  );

  /**
   * A cell is invalid when the row's current value fails the shared validator for that
   * field. The "send" joint error lights both boolean cells; the Flight Type cell surfaces
   * either flight-type error.
   */
  const isCellInvalid = useCallback(
    (row: IGendecRuleRow, field: string): boolean => {
      const errs = errorsByRowId.get(row._rowId);
      if (field === FLIGHT_TYPE_FIELD) return !!(errs?.flightTypeIds ?? errs?.excludedFlightTypeIds);
      const fieldError = errs?.[field as keyof IGendecRuleErrors];
      const sendError = field === "sendOrigin" || field === "sendDestination" ? errs?.send : undefined;
      return !!(fieldError ?? sendError);
    },
    [errorsByRowId]
  );

  /** Update one row's flight-type include/exclude pair from the tri-state popover. */
  const handleFlightTypeChange = useCallback(
    (rowId: string, included: string[], excluded: string[]) => {
      onChange(
        rows.map((r) =>
          r._rowId === rowId
            ? {
                ...r,
                // Round-trip-clean: an empty array becomes undefined so the wire form
                // matches what the dedup signature treats as "no constraint".
                flightTypeIds: included.length > 0 ? included : undefined,
                excludedFlightTypeIds: excluded.length > 0 ? excluded : undefined,
              }
            : r
        )
      );
    },
    [rows, onChange]
  );

  const columns: GridColDef<IGendecRuleRow>[] = [
    dataCol<IGendecRuleRow>({
      field: "flightNumber",
      headerName: "Flight #",
      flex: 12,
      editable: true,
      description: "A number (919), range (920-925), regex (/^9/), or blank for any",
    }),
    dataCol<IGendecRuleRow>({
      field: "origin",
      headerName: "Origin",
      flex: 10,
      editable: true,
      description: "Ident, * (any), or /regex/",
    }),
    dataCol<IGendecRuleRow>({
      field: "destination",
      headerName: "Destination",
      flex: 10,
      editable: true,
      description: "Ident, * (any), or /regex/",
    }),
    dataCol<IGendecRuleRow>({
      field: "format",
      headerName: "Format",
      flex: 8,
      editable: true,
      type: "singleSelect",
      valueOptions: [
        { value: GendecFormats.usa, label: "USA" },
        { value: GendecFormats.can, label: "CAN" },
      ],
    }),
    dataCol<IGendecRuleRow>({
      field: FLIGHT_TYPE_FIELD,
      headerName: "Flight Type",
      flex: 14,
      filterable: false,
      // The popover handles commits directly, so this cell is not editable.
      renderCell: (params: GridRenderCellParams<IGendecRuleRow>): JSX.Element => {
        const row = params.row;
        return (
          <FlightTypeTriStateField
            flightTypes={flightTypes}
            includedIds={row.flightTypeIds}
            excludedIds={row.excludedFlightTypeIds}
            onChange={(inc, exc) => handleFlightTypeChange(row._rowId, inc, exc)}
          />
        );
      },
    }),
    dataCol<IGendecRuleRow>({
      field: "sendOrigin",
      headerName: "Send Origin",
      flex: 8,
      editable: true,
      type: "boolean",
    }),
    dataCol<IGendecRuleRow>({
      field: "sendDestination",
      headerName: "Send Dest",
      flex: 8,
      editable: true,
      type: "boolean",
    }),
    dataCol<IGendecRuleRow>({ field: "batch", headerName: "Batch", flex: 6, editable: true }),
    dataCol<IGendecRuleRow>({
      field: "targetTime",
      headerName: "Target Hour",
      flex: 9,
      editable: true,
      type: "number",
      description: `Hour of day, 0-${GENDEC_TARGET_TIME_MAX}`,
      // HTML5 min/max/step on the inline editor mirror the Add Rule dialog. The shared
      // validator remains the authority on the committed value.
      renderEditCell: integerEditCell<IGendecRuleRow>({ min: 0, max: GENDEC_TARGET_TIME_MAX }),
    }),
    dataCol<IGendecRuleRow>({
      field: "windowStart",
      headerName: "Window Start (min)",
      flex: 9,
      editable: true,
      type: "number",
      description: "Minutes from target time to start of window",
      renderEditCell: integerEditCell<IGendecRuleRow>({ min: 0 }),
    }),
    dataCol<IGendecRuleRow>({
      field: ACTIONS_FIELD,
      headerName: "",
      flex: 7,
      filterable: false,
      renderCell: (params: GridCellParams<IGendecRuleRow>): JSX.Element => (
        <>
          <Tooltip title="Duplicate rule">
            <IconButton size="small" onClick={() => handleDuplicate(params.row)}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete rule">
            <IconButton size="small" onClick={() => handleDelete(params.row)}>
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </>
      ),
    }),
  ];

  return (
    <Box ref={gridContainerRef} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ mb: 1 }}>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          Add Rule
        </Button>
      </Box>
      <AddRuleDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={handleAddRule}
        flightTypes={flightTypes}
      />
      <EditableDataGrid<IGendecRuleRow>
        rows={rows}
        columns={columns}
        getRowId={getRuleRowId}
        onRowsChange={onChange}
        normalizeRow={normalizeRow}
        isCellDirty={isCellDirty}
        isCellInvalid={isCellInvalid}
        sx={{ maxHeight: "calc(100vh - 320px)" }}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[25, 50, 100]}
      />
    </Box>
  );
};

export default RulesGrid;
